import { useCallback, useEffect, useState, useRef } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { FormControl, Grid, InputLabel, MenuItem, Paper, Select, IconButton, Tooltip, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import InfoIcon from '@mui/icons-material/Info';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { RootState } from '../../../reducers';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import b64toBlob from 'b64-to-blob';

import { useWidgetContext } from '../../../hooks/widgetContext';
import { configureStackedBarChart } from '../../ChartConfiguration/ChartConfiguration';
import { fetchTeamInPersonVisitsRate, exportTeamInPersonVisitRate, MenuProps } from '../WidgetService';
import EngagementMemberSummary from '../../../Models/Widget/EngagementMemberSummary.model';
import { TimePeriodFilterEnum } from '../../../Enum/TimePeriodFilterEnum';
import Authorization from '../../../utils/Authorization';
import { getDateFilter } from '../../HedisMetrics/HedisMetricService';
import { fetchTeams } from '../../../store/careTeams.slice';
import TeamDropdown from '../../Shared/TeamDropdown';
import HealthPlanMultiSelect from '../../Shared/HealthPlanMultiSelect';
import '../Widgets.css';

const TeamInPersonVisitRate = (props) => {
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const history = useNavigate();
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const {
        engagementDateFilter,
        setEngagementDateFilter,
        engagementFromDate,
        setEngagementFromDate,
        engagementToDate,
        setEngagementToDate,
        engagementSelectedPlans,
        setEngagementSelectedPlans,
        engagementSelectedTeam,
        setEngagementSelectedTeam,
        engagementShowInActiveTeams,
        setEngagementShowInActiveTeams,
    } = useWidgetContext();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [engagementMemberSummary, setEngagementMemberSummary] = useState<EngagementMemberSummary>();
    const [chartOptions, setChartOptions] = useState({});
    const [show, setShow] = useState(false);
    const ref = useRef(null);
    const chartHeight = 450;

    useEffect(() => {
        dispatch(fetchTeams(true));
    }, [dispatch]);

    const handleClick = useCallback(() => {
        setShow(prev => !prev);
    }, []);

    const dateFilterList = getDateFilter();

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        dispatch(fetchTeams(true));

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChart(
                    'Engagement Summary',
                    'Members Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'Engagement',
                    history,
                    '',
                    '',
                    chartHeight
                )
            );
        }

        setEngagementSelectedTeam(authData.getDefaultTeamId(''));
        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, setIsFetched, isGraphShow, engagementSelectedPlans, history, setEngagementSelectedTeam, dispatch]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const shouldFetch = engagementDateFilter !== TimePeriodFilterEnum.Custom || (engagementFromDate && engagementToDate);

        if (!shouldFetch) {
            return;
        }

        const changeFilter = async () => {
            setIsGraphShow(false);
            const data = await fetchTeamInPersonVisitsRate(
                engagementSelectedPlans.join(','),
                engagementSelectedTeam,
                engagementDateFilter,
                engagementFromDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementFromDate).toISOString() : '',
                engagementToDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementToDate).toISOString() : '',
                engagementShowInActiveTeams,
                false
            );
            setEngagementMemberSummary(data);
        };

        setChartOptions(
            configureStackedBarChart(
                'Engagement Summary',
                'Members Count',
                'Month',
                'Count',
                [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                [0],
                'Engagement',
                history,
                '',
                '',
                chartHeight
            )
        );

        changeFilter();
    }, [
        engagementSelectedPlans,
        engagementDateFilter,
        engagementSelectedTeam,
        engagementFromDate,
        engagementToDate,
        history,
        engagementShowInActiveTeams,
    ]);

    useEffect(() => {
        if (!engagementMemberSummary) return;

        const visitedData = { y: engagementMemberSummary.VisitedMembersCount, name: engagementMemberSummary.VisitedMembersPercentage };
        const notVisitedData = { y: engagementMemberSummary.NotVisitedMembersCount, name: engagementMemberSummary.NotVisitedMembersPercentage };
        const licensedStaffData = { y: engagementMemberSummary.LicensedProviderVisitedMembersCount, name: engagementMemberSummary.LicensedProviderVisitedMembersPercentage };

        const hasNoData = engagementMemberSummary.VisitedMembersPercentage === 0 && engagementMemberSummary.NotVisitedMembersPercentage === 0;

        if (hasNoData) {
            setChartOptions(
                configureStackedBarChart(
                    'Engagement Summary',
                    'Members Count',
                    'Month',
                    'Count',
                    [{ name: '', data: [], showInLegend: false, title: { text: null } }],
                    [0],
                    'Engagement',
                    history,
                    '',
                    '',
                    chartHeight
                )
            );
        } else {
            const chartData = [
                { name: 'Visited', data: [visitedData], color: '#4682B4', showInLegend: true },
                { name: 'Not Visited', data: [notVisitedData], color: '#ff7d7d', showInLegend: true },
                { name: 'Licensed Staff Visited', data: [licensedStaffData], color: '#A569BD', showInLegend: true },
            ];
            setChartOptions(
                configureStackedBarChart('Engagement Chart', '', '', 'Count', chartData, [''], 'Engagement', history,
                    '',
                    '',
                    chartHeight)
            );
        }

        setIsGraphShow(true);
    }, [engagementMemberSummary, history]);

    const changeFromDate = useCallback((date: any) => {
        setEngagementFromDate(date);
    }, [setEngagementFromDate]);

    const changeToDate = useCallback((date: any) => {
        setEngagementToDate(date);
    }, [setEngagementToDate]);

    const navigateToMembers = useCallback(() => {
        history(`/provider/metricsummary/teaminpersonvisitratedetail?hasElevatedFilterAccess=${props.hasElevatedFilterAccess}`);
    }, [history]);

    const exportDetail = async () => {
        setDownLoading(true);
        const data = await exportTeamInPersonVisitRate(
            engagementSelectedPlans.join(','),
            engagementSelectedTeam,
            engagementDateFilter,
            engagementFromDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementFromDate).toISOString() : '',
            engagementToDate && engagementDateFilter === TimePeriodFilterEnum.Custom ? new Date(engagementToDate).toISOString() : '',
            engagementShowInActiveTeams
        );

        const blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const today = new Date();
        const dateInfo = today.toISOString().split('T')[0].replace(/-/g, '');
        link.setAttribute('download', `TeamInPersonVisitRate - ${dateInfo}.xlsx`);
        link.click();
        link.remove();

        setDownLoading(false);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                    <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                        <Grid size={12} textAlign="center">
                            <Typography display="inline-block" variant="h6" marginRight={1} color="primary" ref={ref}>
                                <span className="pointer" onClick={navigateToMembers}>
                                    Team In Person Visit Rate
                                </span>
                                <Tooltip
                                    open={show}
                                    title={
                                        <div>
                                            <Typography variant="caption">
                                                This widget will display all of the members assigned to a team.
                                                The widget will reflect how many members have had a single in person visit by any provider on the team and also indicate whether that visit was by a licensed provider during the selected date range.
                                                The widget will also reflect how many members have not had an in person visit by any provider during the selected date range.
                                                This widget will help you understand how well you are meeting your members in person.
                                            </Typography>
                                        </div>
                                    }
                                    PopperProps={{ style: { zIndex: 1000 } }}
                                >
                                    <IconButton onClick={handleClick}>
                                        <InfoIcon sx={{ width: '18px', height: '18px' }} />
                                    </IconButton>
                                </Tooltip>
                            </Typography>
                        </Grid>
                        {props.hasElevatedFilterAccess && (
                            <Grid size={4}>
                                <TeamDropdown
                                    teams={teams}
                                    selectedTeam={engagementSelectedTeam}
                                    onChange={(id) => setEngagementSelectedTeam(id)}
                                    showInactive={engagementShowInActiveTeams}
                                    onToggleInactive={setEngagementShowInActiveTeams}
                                />
                            </Grid>
                        )}
                        <Grid size={4}>
                            <HealthPlanMultiSelect
                                value={engagementSelectedPlans}
                                onChange={setEngagementSelectedPlans}
                                menuProps={MenuProps}
                            />
                        </Grid>
                        <Grid size={3.6}>
                            <FormControl size="small" fullWidth>
                                <InputLabel>Date Filter</InputLabel>
                                <Select
                                    value={engagementDateFilter}
                                    label="Date Filter"
                                    name="Date"
                                    onChange={(e) => setEngagementDateFilter(parseInt(e.target.value as any))}
                                >
                                    {dateFilterList.map((date) => (
                                        <MenuItem key={date.Id} value={date.Id}>
                                            {date.Value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        {engagementDateFilter === TimePeriodFilterEnum.Custom ? (
                            <>
                                <Grid size={3.1}>
                                    <DesktopDatePicker
                                        label="From Date"
                                        value={engagementFromDate ?? null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={changeFromDate}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                <Grid size={3.1}>
                                    <DesktopDatePicker
                                        label="To Date"
                                        value={engagementToDate ?? null}
                                        minDate={new Date('2017-01-01')}
                                        onChange={changeToDate}
                                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                                    />
                                </Grid>
                                {!props.hasElevatedFilterAccess ? (
                                    <Grid size={5.2}>
                                    </Grid>
                                ) : <Grid size={3}>
                                </Grid>}
                            </>
                        ) : <> {!props.hasElevatedFilterAccess ? (
                            <Grid size={2}>
                            </Grid>
                        ) : <Grid size={9.2}>
                        </Grid>}</>}
                        <Grid size={1}>
                            <Button
                                style={{ minWidth: '120px', marginLeft: 'auto' }}
                                onClick={() => exportDetail()}
                                variant="contained"
                                color="success"
                                disabled={!Boolean(engagementMemberSummary) || (engagementMemberSummary?.VisitedMembersCount === 0 && engagementMemberSummary?.NotVisitedMembersCount === 0 && engagementMemberSummary?.LicensedProviderVisitedMembersCount === 0) || downLoading}
                            >
                                {downLoading ? 'Exporting...' : 'Export'}
                            </Button>
                        </Grid>
                        <Grid size={12} sx={{ position: 'relative' }}>
                            {!isGraphShow && (
                                <Box className="loader-center">
                                    <CircularProgress />
                                </Box>
                            )}
                            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </LocalizationProvider>
    );
};

export default TeamInPersonVisitRate;
